import React, { Fragment, useEffect, useState, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { Container, Modal, ModalHeader, ModalBody, ModalFooter, Nav, NavItem, NavLink, TabContent, TabPane } from "reactstrap";
import Breadcrumb from "../common/breadcrumb";
import { C, tbl, reportingGlobalCSS } from "../reporting/reportingStyles";
import { Tile, RCard, Badge, Loader } from "../reporting/ReportingShared";
import {
    getWalletPasses,
    getWalletPassDetails,
    forceRefreshWalletPass,
} from "../../utils/apiCalls";

const PAGE_LIMIT = 25;

// ── helpers ───────────────────────────────────────────────────
const formatAgo = (iso) => {
    if (!iso) return "—";
    const sec = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
    if (sec < 60) return "just now";
    if (sec < 3600) return `${Math.floor(sec / 60)} min ago`;
    if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
    return `${Math.floor(sec / 86400)}d ago`;
};
const formatDate = (iso) => iso ? new Date(iso).toLocaleString() : "—";

const PassStatusBadge = ({ status, deviceCount }) => {
    if (status === "installed") {
        return (
            <Badge color={C.green} bg={C.greenLight}>
                ● Installed · {deviceCount} {deviceCount === 1 ? "device" : "devices"}
            </Badge>
        );
    }
    return <Badge color={C.orange} bg={C.orangeLight}>● Generated</Badge>;
};

// ── input styles to match reporting ────────────────────────────
const inputStyle = {
    padding: "10px 14px", borderRadius: 10, fontSize: 13,
    border: `1px solid ${C.border}`, background: C.white,
    color: C.text, outline: "none", transition: "border-color 0.15s",
};
const buttonStyle = {
    padding: "10px 18px", borderRadius: 10, fontSize: 13, fontWeight: 700,
    border: "none", cursor: "pointer", transition: "all 0.15s",
    background: `linear-gradient(135deg, ${C.coral}, #00bfa5)`, color: C.white,
    boxShadow: "0 4px 12px rgba(0, 150, 137,0.25)",
};
const btnSecondary = {
    ...buttonStyle, background: C.white, color: C.textMed,
    border: `1px solid ${C.border}`, boxShadow: "none",
};
const btnWarning = {
    ...buttonStyle,
    background: `linear-gradient(135deg, ${C.orange}, #FFB347)`,
    boxShadow: "0 4px 12px rgba(247,151,30,0.25)",
};

// ── page ───────────────────────────────────────────────────────
const WalletPasses = () => {
    const userType = localStorage.getItem("userType");
    const loggedInMerchantId = localStorage.getItem("MerchantId");
    const { selectedMerchant: reduxSelectedMerchant } = useSelector((state) => state.merchant || {});
    const merchantId = userType === "merchant" ? loggedInMerchantId : reduxSelectedMerchant;

    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState("");
    const [status, setStatus] = useState("all");
    const [loading, setLoading] = useState(false);

    const [selectedPassId, setSelectedPassId] = useState(null);
    const [details, setDetails] = useState(null);
    const [detailsLoading, setDetailsLoading] = useState(false);
    const [activeTab, setActiveTab] = useState("devices");
    const [confirmRefresh, setConfirmRefresh] = useState(false);
    const [refreshing, setRefreshing] = useState(false);

    const fetchList = useCallback(async () => {
        if (!merchantId || merchantId === "all") {
            setItems([]); setTotal(0); return;
        }
        setLoading(true);
        const data = await getWalletPasses({ merchantId, search, status, page, limit: PAGE_LIMIT });
        setItems(data.items || []);
        setTotal(data.total || 0);
        setLoading(false);
    }, [merchantId, search, status, page]);

    useEffect(() => { fetchList(); }, [fetchList]);

    const openDetails = async (passId) => {
        setSelectedPassId(passId);
        setDetailsLoading(true);
        const d = await getWalletPassDetails(passId);
        setDetails(d);
        setDetailsLoading(false);
    };
    const closeDetails = () => {
        setSelectedPassId(null); setDetails(null); setActiveTab("devices");
    };

    const handleForceRefresh = async () => {
        if (!selectedPassId) return;
        setRefreshing(true);
        await forceRefreshWalletPass(selectedPassId);
        setRefreshing(false);
        setConfirmRefresh(false);
        const d = await getWalletPassDetails(selectedPassId);
        setDetails(d);
        fetchList();
    };

    const totalPages = Math.max(1, Math.ceil(total / PAGE_LIMIT));

    // ── derive tile metrics from current page (or use total counts) ──
    const tiles = useMemo(() => {
        const installed = items.filter(i => i.status === "installed").length;
        const devices = items.reduce((s, i) => s + (i.deviceCount || 0), 0);
        return { installed, devices };
    }, [items]);

    // ── render ─────────────────────────────────────────────────
    return (
        <Fragment>
            <Breadcrumb title="Wallet Passes" parent="Loyalty" />
            <Container fluid>
                <style>{reportingGlobalCSS}</style>
                <div className="rpt-wrap">

                    {/* metric tiles */}
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 16, marginBottom: 20 }}>
                        <Tile
                            label="Total Passes"
                            value={total}
                            sub="Generated by customers"
                            icon="📥"
                            gradient={`linear-gradient(135deg, ${C.blue}, #A8B8FF)`}
                            delay={0}
                        />
                        <Tile
                            label="Installed (this page)"
                            value={tiles.installed}
                            sub={`of ${items.length} shown`}
                            icon="📲"
                            gradient={`linear-gradient(135deg, ${C.green}, #38F9D7)`}
                            delay={80}
                        />
                        <Tile
                            label="Active Devices (this page)"
                            value={tiles.devices}
                            sub="iPhones / iPads receiving updates"
                            icon="🍎"
                            gradient={`linear-gradient(135deg, ${C.orange}, #FFB347)`}
                            delay={160}
                        />
                    </div>

                    {/* filters */}
                    <RCard>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center" }}>
                            <input
                                type="search"
                                placeholder="Search by name or phone…"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                onKeyDown={(e) => { if (e.key === "Enter") { setPage(1); fetchList(); } }}
                                style={{ ...inputStyle, minWidth: 260, flex: 1 }}
                            />
                            <select
                                value={status}
                                onChange={(e) => { setStatus(e.target.value); setPage(1); }}
                                style={{ ...inputStyle, minWidth: 220 }}
                            >
                                <option value="all">All passes</option>
                                <option value="installed">Installed only</option>
                                <option value="generated">Generated, not installed</option>
                            </select>
                            <button onClick={() => { setPage(1); fetchList(); }} style={buttonStyle}>
                                Search
                            </button>
                        </div>
                    </RCard>

                    {/* table */}
                    <RCard
                        title={
                            <span>📲 Apple Wallet Passes <span style={{ color: C.textLight, fontWeight: 500, marginLeft: 8 }}>· {total} total</span></span>
                        }
                    >
                        {!merchantId || merchantId === "all" ? (
                            <div style={{ textAlign: "center", padding: 40, color: C.textMed }}>Please select a merchant.</div>
                        ) : loading ? (
                            <Loader />
                        ) : items.length === 0 ? (
                            <div style={{ textAlign: "center", padding: 40, color: C.textMed }}>No wallet passes found.</div>
                        ) : (
                            <>
                                <div style={{ overflowX: "auto" }}>
                                    <table style={tbl.table}>
                                        <thead>
                                            <tr>
                                                <th style={tbl.th}>Customer</th>
                                                <th style={tbl.th}>Phone</th>
                                                <th style={tbl.th}>Serial</th>
                                                <th style={tbl.th}>Generated</th>
                                                <th style={tbl.th}>Status</th>
                                                <th style={tbl.th}>Last Updated</th>
                                                <th style={tbl.th}></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {items.map((p) => (
                                                <tr key={p.id} className="rpt-row">
                                                    <td style={{ ...tbl.td, fontWeight: 600, color: C.text }}>{p.customer?.name || "—"}</td>
                                                    <td style={{ ...tbl.td, color: C.textMed }}>{p.customer?.phone || "—"}</td>
                                                    <td style={{ ...tbl.td, fontFamily: "ui-monospace, SFMono-Regular, monospace", fontSize: 12, color: C.textMed }} title={p.serialNumber}>
                                                        {p.serialNumber.slice(0, 8)}…
                                                    </td>
                                                    <td style={{ ...tbl.td, color: C.textMed }} title={formatDate(p.generatedAt)}>{formatAgo(p.generatedAt)}</td>
                                                    <td style={tbl.td}>
                                                        <PassStatusBadge status={p.status} deviceCount={p.deviceCount} />
                                                    </td>
                                                    <td style={{ ...tbl.td, color: C.textMed }} title={formatDate(p.lastUpdatedAt)}>{formatAgo(p.lastUpdatedAt)}</td>
                                                    <td style={tbl.td}>
                                                        <button onClick={() => openDetails(p.id)} style={{ ...btnSecondary, padding: "6px 12px", fontSize: 12 }}>
                                                            View details
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                {totalPages > 1 && (
                                    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 16, marginTop: 24, color: C.textMed, fontSize: 13 }}>
                                        <button style={{ ...btnSecondary, padding: "8px 16px", fontSize: 12, opacity: page <= 1 ? 0.5 : 1 }} disabled={page <= 1} onClick={() => setPage(page - 1)}>← Prev</button>
                                        <span>Page <strong style={{ color: C.text }}>{page}</strong> of {totalPages}</span>
                                        <button style={{ ...btnSecondary, padding: "8px 16px", fontSize: 12, opacity: page >= totalPages ? 0.5 : 1 }} disabled={page >= totalPages} onClick={() => setPage(page + 1)}>Next →</button>
                                    </div>
                                )}
                            </>
                        )}
                    </RCard>
                </div>
            </Container>

            {/* Details modal */}
            <Modal isOpen={!!selectedPassId} toggle={closeDetails} size="lg">
                <ModalHeader toggle={closeDetails}>
                    <span style={{ fontWeight: 700 }}>{details?.customer?.name || "Pass details"}</span>
                    {details?.customer?.phone && <span style={{ color: C.textLight, fontWeight: 500, marginLeft: 8, fontSize: 14 }}>· {details.customer.phone}</span>}
                </ModalHeader>
                <ModalBody>
                    {detailsLoading ? (
                        <Loader />
                    ) : !details ? (
                        <div style={{ color: C.textMed, padding: 12 }}>Failed to load.</div>
                    ) : (
                        <>
                            <div style={{ background: C.bg, padding: 14, borderRadius: 10, marginBottom: 16, fontSize: 13, border: `1px solid ${C.border}` }}>
                                <div style={{ display: "grid", gridTemplateColumns: "120px 1fr", rowGap: 6, columnGap: 12, color: C.textMed }}>
                                    <div style={{ fontWeight: 600 }}>Serial</div>
                                    <div style={{ fontFamily: "ui-monospace, SFMono-Regular, monospace", color: C.text }}>{details.pass.serialNumber}</div>
                                    <div style={{ fontWeight: 600 }}>Pass Type</div>
                                    <div style={{ fontFamily: "ui-monospace, SFMono-Regular, monospace", color: C.text }}>{details.pass.passTypeIdentifier}</div>
                                    <div style={{ fontWeight: 600 }}>Generated</div>
                                    <div style={{ color: C.text }}>{formatDate(details.pass.generatedAt)}</div>
                                    <div style={{ fontWeight: 600 }}>Last Updated</div>
                                    <div style={{ color: C.text }}>{formatDate(details.pass.lastUpdatedAt)} <span style={{ color: C.textLight }}>({formatAgo(details.pass.lastUpdatedAt)})</span></div>
                                </div>
                            </div>

                            <Nav tabs style={{ borderBottom: `1px solid ${C.border}` }}>
                                <NavItem>
                                    <NavLink active={activeTab === "devices"} onClick={() => setActiveTab("devices")} style={{ cursor: "pointer", color: activeTab === "devices" ? C.coral : C.textMed, fontWeight: 600 }}>
                                        Devices ({details.devices.length})
                                    </NavLink>
                                </NavItem>
                                <NavItem>
                                    <NavLink active={activeTab === "logs"} onClick={() => setActiveTab("logs")} style={{ cursor: "pointer", color: activeTab === "logs" ? C.coral : C.textMed, fontWeight: 600 }}>
                                        Device Logs ({details.logs.length})
                                    </NavLink>
                                </NavItem>
                            </Nav>
                            <TabContent activeTab={activeTab} style={{ paddingTop: 16 }}>
                                <TabPane tabId="devices">
                                    {details.devices.length === 0 ? (
                                        <div style={{ color: C.textMed, padding: 16, textAlign: "center" }}>No devices have installed this pass yet.</div>
                                    ) : (
                                        <div style={{ overflowX: "auto" }}>
                                            <table style={tbl.table}>
                                                <thead>
                                                    <tr>
                                                        <th style={tbl.th}>Device ID</th>
                                                        <th style={tbl.th}>Push Token</th>
                                                        <th style={tbl.th}>Registered</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {details.devices.map((d) => (
                                                        <tr key={d.id} className="rpt-row">
                                                            <td style={{ ...tbl.td, fontFamily: "ui-monospace, SFMono-Regular, monospace", fontSize: 12, color: C.textMed }} title={d.deviceLibraryIdentifier}>
                                                                {d.deviceLibraryIdentifier.slice(0, 12)}…
                                                            </td>
                                                            <td style={{ ...tbl.td, fontFamily: "ui-monospace, SFMono-Regular, monospace", fontSize: 12, color: C.textMed }}>{d.pushTokenPreview}</td>
                                                            <td style={{ ...tbl.td, color: C.textMed }} title={formatDate(d.registeredAt)}>{formatAgo(d.registeredAt)}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </TabPane>
                                <TabPane tabId="logs">
                                    {details.logs.length === 0 ? (
                                        <div style={{ color: C.textMed, padding: 16, textAlign: "center" }}>No device-side errors logged for this serial yet.</div>
                                    ) : (
                                        <div style={{ maxHeight: 380, overflowY: "auto" }}>
                                            {details.logs.map((l) => (
                                                <div key={l.id} style={{ padding: "10px 12px", borderBottom: `1px solid ${C.borderLight}`, fontSize: 12 }}>
                                                    <div style={{ color: C.textLight, marginBottom: 4 }}>{formatDate(l.createdAt)}</div>
                                                    <div style={{ fontFamily: "ui-monospace, SFMono-Regular, monospace", whiteSpace: "pre-wrap", color: C.text }}>{l.message}</div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </TabPane>
                            </TabContent>
                        </>
                    )}
                </ModalBody>
                <ModalFooter style={{ borderTop: `1px solid ${C.border}` }}>
                    <button onClick={closeDetails} style={btnSecondary}>Close</button>
                    {details && (
                        <button onClick={() => setConfirmRefresh(true)} disabled={refreshing} style={btnWarning}>
                            🔄 Force Pass Refresh
                        </button>
                    )}
                </ModalFooter>
            </Modal>

            {/* Confirm */}
            <Modal isOpen={confirmRefresh} toggle={() => setConfirmRefresh(false)}>
                <ModalHeader toggle={() => setConfirmRefresh(false)}>Force Pass Refresh?</ModalHeader>
                <ModalBody style={{ color: C.textMed, lineHeight: 1.55 }}>
                    This sends a push notification to all of <strong style={{ color: C.text }}>{details?.customer?.name || "this customer"}</strong>'s
                    {" "}registered devices, asking them to refetch the latest pass.
                    Use this when you've corrected stale data manually or after a config change.
                </ModalBody>
                <ModalFooter style={{ borderTop: `1px solid ${C.border}` }}>
                    <button onClick={() => setConfirmRefresh(false)} style={btnSecondary}>Cancel</button>
                    <button onClick={handleForceRefresh} disabled={refreshing} style={btnWarning}>
                        {refreshing ? "Sending…" : "Send Push"}
                    </button>
                </ModalFooter>
            </Modal>
        </Fragment>
    );
};

export default WalletPasses;
